import type { PromotionRewardType } from "~/graphql-client/generated";
import type { Product } from "~/types";
import type { SharedDealFields } from "~/utils/deal";
import { ClaimSuccessModal } from "../ClaimPromotionModal/ClaimSuccessModal";
import { DealUnavailableDialog } from "../DealUnavailableDialog/DealUnavailableDialog";
import { ExclusiveDealConflictDialog } from "../ExclusiveDealConflictDialog/ExclusiveDealConflictDialog";
import type { ModalWidth } from "../Modal/Modal";
import RedeemCodeError from "./RedeemCodeError";
import {
  DaveDeal,
  VoucherCodeDeal,
  type VoucherCodeDealProps,
} from "./VoucherCodeDeal";
import { VoucherCodeDialog } from "./VoucherCodeDialog/VoucherCodeDialog";
import { VoucherCodeNotLoggedIn } from "./VoucherCodeNotLoggedIn";

export interface VoucherCodeOutcomeProps {
  code: string;
  busy?: boolean;
  daveProduct?: Product;
  dealProps: Omit<VoucherCodeDealProps, "busy" | "onClose"> | null;
  notLoggedIn?: boolean;
  error?: string;
  voucher?: {
    title: string;
    message: string;
    rewardType: PromotionRewardType | null;
  };
  dealConflict?: {
    newDealTitle: string;
    newDealExclusive: boolean;
    dealsToRemove: SharedDealFields[];
  };
  dealUnavailable?: boolean;
  onHide: () => void;
  onContinue: () => void;
  onLogin: () => void;
  onReplaceConflictingDeals: () => void;
  conflictDialogWidth?: ModalWidth;
}

/**
 * The dialogs shown after a promo code is submitted. These are fixed
 * position, so render them outside any transformed container (e.g. a Drawer).
 */
export function VoucherCodeOutcome(props: VoucherCodeOutcomeProps) {
  return (
    <>
      {props.daveProduct && (
        <DaveDeal product={props.daveProduct} onClick={props.onHide} />
      )}

      {props.dealProps && (
        <VoucherCodeDeal
          busy={props.busy}
          onClose={props.onHide}
          {...props.dealProps}
        />
      )}

      <DealUnavailableDialog
        open={Boolean(props.dealUnavailable)}
        onClose={props.onHide}
        modalWidth={props.conflictDialogWidth}
      />

      <ExclusiveDealConflictDialog
        open={props.dealConflict !== undefined}
        newDealTitle={props.dealConflict?.newDealTitle}
        newDealExclusive={props.dealConflict?.newDealExclusive ?? false}
        dealsToRemove={props.dealConflict?.dealsToRemove ?? []}
        onReplace={props.onReplaceConflictingDeals}
        onKeepCurrent={props.onHide}
        modalWidth={props.conflictDialogWidth}
      />

      {props.notLoggedIn && (
        <VoucherCodeNotLoggedIn
          code={props.code}
          onClick={props.onHide}
          onClickLogin={props.onLogin}
        />
      )}

      {props.error && (
        <VoucherCodeDialog>
          <RedeemCodeError
            code={props.code}
            message={props.error}
            onRetryVoucherCode={props.onHide}
          />
        </VoucherCodeDialog>
      )}

      {props.voucher && (
        <ClaimSuccessModal
          onClose={props.onContinue}
          collectionDescription={props.voucher.message}
          rewardType={props.voucher.rewardType}
        />
      )}
    </>
  );
}
